/**
 * ログインの失敗の内容（ログイン画面に出すもの）。
 *
 * @remarks
 * Microsoft（Entra）がサインインの失敗を返すと、Auth.js はログイン画面へ `?error=<種類>` だけを付けて戻す。
 * コールバックのルート（`lib/server/sign-in-error-redirect.ts`）が Microsoft の `error`・`error_description`
 * から下の項目を取り出してログイン画面の URL に足し、ログイン画面がここで読み直して表示する。
 * Microsoft のエラー文はそのまま出さず、形の決まった項目（番号・コード・ID・日時）だけを通す。
 * ログイン画面の URL は誰でも作れるので、好きな文を入れたリンクで画面の表示を偽れないようにするため。
 */
export type SignInErrorInfo = {
  /** Auth.js のエラーの種類（`OAuthCallbackError`・`Configuration` など）。 */
  type: string;
  /** Microsoft のエラーコードの番号（`AADSTS65004` の `65004`）。 */
  aadsts?: string;
  /** OAuth のエラーの種類（`consent_required` など）。 */
  oauthError?: string;
  /** Microsoft の Trace ID。 */
  traceId?: string;
  /** Microsoft の Correlation ID。 */
  correlationId?: string;
  /** Microsoft がエラーを返した日時（UTC・`2026-10-23 05:44:17Z` の形）。 */
  timestamp?: string;
};

type DetailKey = Exclude<keyof SignInErrorInfo, "type">;

/** ログイン画面の URL で Microsoft のエラーの項目に使うクエリの名前。 */
export const SIGN_IN_ERROR_PARAMS: Readonly<Record<DetailKey, string>> = {
  aadsts: "aadsts",
  oauthError: "oauth_error",
  traceId: "trace_id",
  correlationId: "correlation_id",
  timestamp: "timestamp",
};

// Auth.js がログイン画面へ渡す種類（クライアントに出してよい種類と、それ以外をまとめた Configuration）
const AUTH_ERROR_TYPES: ReadonlySet<string> = new Set([
  "Configuration",
  "AccessDenied",
  "Verification",
  "OAuthCallbackError",
  "OAuthAccountNotLinked",
  "AccountNotLinked",
  "MissingCSRF",
  "CredentialsSignin",
  "WebAuthnVerificationError",
]);

// 認可応答とトークンの応答の error の値（OAuth 2.0・OpenID Connect と、Microsoft が返すもの）
const OAUTH_ERRORS: ReadonlySet<string> = new Set([
  "invalid_request",
  "invalid_client",
  "invalid_grant",
  "unauthorized_client",
  "unsupported_grant_type",
  "access_denied",
  "unsupported_response_type",
  "invalid_scope",
  "server_error",
  "temporarily_unavailable",
  "invalid_resource",
  "interaction_required",
  "login_required",
  "account_selection_required",
  "consent_required",
  "invalid_request_uri",
  "invalid_request_object",
  "request_not_supported",
  "request_uri_not_supported",
  "registration_not_supported",
]);

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// 種類は決まった値の一覧で、番号・ID・日時は形で検査する（文字の種類だけで通すと、英字だけの
// 好きな語を「エラーの種類」として出せてしまう）
const VALID: Readonly<Record<keyof SignInErrorInfo, (value: string) => boolean>> = {
  type: (v) => AUTH_ERROR_TYPES.has(v),
  aadsts: (v) => /^\d{4,9}$/.test(v),
  oauthError: (v) => OAUTH_ERRORS.has(v),
  traceId: (v) => GUID.test(v),
  correlationId: (v) => GUID.test(v),
  timestamp: (v) => /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}Z$/.test(v),
};

/**
 * Microsoft が返した `error`・`error_description` から、ログイン画面に足すクエリを作る。
 *
 * @param error - 認可応答の `error`（OAuth のエラーの種類）。
 * @param description - 認可応答の `error_description`（`AADSTS65004: … Trace ID: … Correlation ID: … Timestamp: …`）。
 * @returns 一覧・形に合う項目だけのクエリ（名前は {@link SIGN_IN_ERROR_PARAMS}）。
 */
export function providerErrorParams(
  error: string | null,
  description: string | null,
): Record<string, string> {
  const text = description ?? "";
  const found: Partial<Record<DetailKey, string | undefined>> = {
    aadsts: text.match(/\bAADSTS(\d{4,9})\b/)?.[1],
    oauthError: error ?? undefined,
    traceId: text.match(/Trace ID:\s*([0-9a-f-]{36})/i)?.[1],
    correlationId: text.match(/Correlation ID:\s*([0-9a-f-]{36})/i)?.[1],
    timestamp: text.match(/Timestamp:\s*(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}Z)/)?.[1],
  };
  const params: Record<string, string> = {};
  for (const key of Object.keys(SIGN_IN_ERROR_PARAMS) as DetailKey[]) {
    const value = found[key];
    if (value && VALID[key](value)) params[SIGN_IN_ERROR_PARAMS[key]] = value;
  }
  return params;
}

const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

/**
 * ログイン画面のクエリから、表示する失敗の内容を組み立てる。
 *
 * @param query - ログイン画面の `searchParams`。
 * @returns `error` が無ければ null。一覧・形に合わない項目は捨てる（種類が一覧に無ければ `Unknown`）。
 */
export function parseSignInError(
  query: Readonly<Record<string, string | string[] | undefined>>,
): SignInErrorInfo | null {
  const type = first(query.error);
  if (!type) return null;
  const info: SignInErrorInfo = { type: VALID.type(type) ? type : "Unknown" };
  for (const key of Object.keys(SIGN_IN_ERROR_PARAMS) as DetailKey[]) {
    const value = first(query[SIGN_IN_ERROR_PARAMS[key]]);
    if (value && VALID[key](value)) info[key] = value;
  }
  return info;
}

// 主な Microsoft のエラーコードの説明（Nexus の要求で起こりうるもの）。ほかのコードは説明を出さず、
// Microsoft のエラーの説明ページへのリンクで補う。
const AADSTS_MESSAGES: Readonly<Record<string, string>> = {
  "50020":
    "このアカウントは、Nexus を登録した組織（テナント）にありません。別の組織のアカウントは、ゲストとして招待され、招待を承諾している必要があります。",
  "50105":
    "このアカウントは、Nexus のアプリに割り当てられていません。Microsoft Entra の管理者が、エンタープライズ アプリケーションの「ユーザーとグループ」にアカウントかグループを割り当てる必要があります。",
  "53003": "条件付きアクセスのポリシーで、サインインがブロックされました。",
  "54005": "サインインの結果（認可コード）がすでに使われています。もう一度ログインしてください。",
  "65001":
    "Nexus のアプリを使う同意がありません。Microsoft Entra の管理者が、アプリ登録の API のアクセス許可（Microsoft Graph の openid・profile・email・offline_access）に管理者の同意を付ける必要があります。",
  "65004":
    "アクセス許可の確認の画面で同意されませんでした。同意できないときは、Microsoft Entra の管理者がアプリ登録に管理者の同意を付ける必要があります。",
  "90094":
    "Nexus のアプリを使うには、Microsoft Entra の管理者の承認が必要です。管理者が、アプリ登録の API のアクセス許可に管理者の同意を付ける必要があります。",
  "90095":
    "Nexus のアプリを使うには、Microsoft Entra の管理者の承認が必要です。管理者が、アプリ登録の API のアクセス許可に管理者の同意を付ける必要があります。",
  "70008": "サインインの結果（認可コード）の期限が切れました。もう一度ログインしてください。",
  "700016":
    "Nexus のアプリ登録が見つかりません。Nexus に設定したテナント ID・クライアント ID を確かめる必要があります。",
  "7000215":
    "Nexus に設定したクライアントシークレットが正しくありません。運用者が、アプリ登録のクライアントシークレットの「値」（シークレット ID ではない）を Nexus に設定し直す必要があります。",
  "7000218":
    "Nexus にクライアントシークレットが設定されていません。運用者が、アプリ登録のクライアントシークレットの値を Nexus に設定する必要があります。",
  "7000222":
    "Nexus に設定したクライアントシークレットの期限が切れています。運用者が、アプリ登録で新しいクライアントシークレットを作り、Nexus に設定し直す必要があります。",
};

const TYPE_MESSAGES: Readonly<Record<string, string>> = {
  Configuration: "ログインの設定に問題があります。",
  AccessDenied: "このアカウントではログインできません。",
};

/**
 * 失敗の内容の説明（主な Microsoft のエラーコードと Auth.js の種類だけ）。
 *
 * @returns 説明が無いものは null。
 */
export function describeSignInError(info: SignInErrorInfo): string | null {
  if (info.aadsts) return AADSTS_MESSAGES[info.aadsts] ?? null;
  return TYPE_MESSAGES[info.type] ?? null;
}

/**
 * 運用者に伝える項目を、表示の順に並べる（ラベルと値）。
 */
export function signInErrorDetails(info: SignInErrorInfo): Array<[label: string, value: string]> {
  const rows: Array<[string, string | undefined]> = [
    ["Microsoft のエラーコード", info.aadsts && `AADSTS${info.aadsts}`],
    ["エラーの種類", info.oauthError ?? info.type],
    ["Trace ID", info.traceId],
    ["Correlation ID", info.correlationId],
    ["日時（UTC）", info.timestamp],
  ];
  return rows.filter((row): row is [string, string] => !!row[1]);
}

/**
 * Microsoft のエラーコードの説明ページ（英語・Microsoft の公開ページ）の URL。
 */
export function microsoftErrorUrl(aadsts: string): string {
  return `https://login.microsoftonline.com/error?code=${encodeURIComponent(aadsts)}`;
}
